import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { CompanySettingsModel } from '../models'
import { CompanySettingsApi } from '../../services/CompanySettingsApi'
import { toastSuccess } from '../toastr'

export type CompanyLegalType = 'doo' | 'jdoo' | 'dd' | 'obrt' | 'rest'
type StringYesNo = 'yes' | 'no'

export type OnboardingCompanyData = CompanySettingsModel & {
    userFullName?: string
    companyLegalType?: CompanyLegalType
    bankAccounts?: string[]
    vatOnCollectedPayments?: StringYesNo
    workingWithIndividuals?: StringYesNo
    commercialCourtName: string
    companyRegistrationNumber: string
    shareCapitalAmount?: string
    shareCapitalFullyPaid?: StringYesNo
    unpaidShareCapitalAmount?: string
    boardMembers: string[]
    doYouHaveAnAdvisoryBoard?: StringYesNo
    advisoryBoardPresidentName?: string
    numberOfIssuedShares?: string
    nominalValuePerShare?: string
    differentAddressForOwnerAndTrade?: StringYesNo
    tradeOwnerName?: string
    tradeOwnerAddress?: string
}

interface OnboardingState {
    // Modal visibility
    isOnboardingModalOpen: boolean

    // Flag to show onboarding on app start (after registration)
    showOnboardingOnStart: boolean

    // Loading states
    isLoadingOnboardingData: boolean
    isSubmittingOnboardingData: boolean

    // Data
    settings: CompanySettingsModel | null
    onboardingData: OnboardingCompanyData | null
    pendingAutofill: Partial<OnboardingCompanyData> | null
}

interface OnboardingActions {
    // Modal controls
    setIsOnboardingModalOpen: (open: boolean) => void
    closeOnboardingModal: () => void

    // Flag control
    setShowOnboardingOnStart: (show: boolean) => void

    // Initialize onboarding data from company settings
    initializeOnboarding: (
        companySettings: CompanySettingsModel,
        userFirstName: string,
        userLastName: string,
        updateLanguage: (language: string) => void,
        setCompanySettings: (settings: CompanySettingsModel) => void
    ) => void

    // Update function
    updateOnboardingData: (
        partialOnboardingData: Partial<OnboardingCompanyData>,
        updateLanguage: (language: string) => void,
        setCompanySettings: (settings: CompanySettingsModel) => void,
        skipSuccessToast?: boolean
    ) => Promise<void>

    setPendingAutofill: (partial: Partial<OnboardingCompanyData> | null) => void

    // Reset state
    reset: () => void
}

type OnboardingStore = OnboardingState & OnboardingActions

const defaultState: OnboardingState = {
    isOnboardingModalOpen: false,
    showOnboardingOnStart: false,
    isLoadingOnboardingData: true,
    isSubmittingOnboardingData: false,
    settings: null,
    onboardingData: null,
    pendingAutofill: null,
}

function isEmptyOrDefaultOIB(oib: string | null): boolean {
    return oib === null || oib === '' || oib === '12345678901'
}

export const useOnboardingStore = create<OnboardingStore>()(
    persist(
        (set, get) => ({
            ...defaultState,

            setIsOnboardingModalOpen: (open: boolean) => {
                set({ isOnboardingModalOpen: open })
            },

            closeOnboardingModal: () => {
                set({ isOnboardingModalOpen: false })
            },

            setShowOnboardingOnStart: (show: boolean) => {
                set({ showOnboardingOnStart: show })
            },

            initializeOnboarding: (
                companySettings: CompanySettingsModel,
                userFirstName: string,
                userLastName: string,
                updateLanguage: (language: string) => void,
                setCompanySettings: (settings: CompanySettingsModel) => void
            ) => {
                set({ isLoadingOnboardingData: true })

                const { pendingAutofill } = get()

                const onboardingData: OnboardingCompanyData = {
                    ...companySettings,
                    ...(pendingAutofill || {}),
                    userFullName: `${userFirstName} ${userLastName}`,
                    companyLegalType: undefined,
                    bankAccounts: [],
                    vatOnCollectedPayments: 'no' as StringYesNo,
                    workingWithIndividuals: 'no' as StringYesNo,
                    commercialCourtName: '',
                    companyRegistrationNumber: '',
                    shareCapitalAmount: undefined,
                    shareCapitalFullyPaid: 'yes' as StringYesNo,
                    unpaidShareCapitalAmount: undefined,
                    boardMembers: [''],
                    doYouHaveAnAdvisoryBoard: 'no' as StringYesNo,
                    advisoryBoardPresidentName: '',
                    differentAddressForOwnerAndTrade: 'no' as StringYesNo,
                }

                // Check if we should show the onboarding modal
                const shouldShowModal = get().showOnboardingOnStart || isEmptyOrDefaultOIB(companySettings.oib)

                console.log('shouldShowModal', shouldShowModal)

                set({
                    settings: companySettings,
                    onboardingData,
                    isLoadingOnboardingData: false,
                    isOnboardingModalOpen: shouldShowModal,
                    pendingAutofill: null,
                })
            },

            updateOnboardingData: async (
                partialOnboardingData: Partial<OnboardingCompanyData>,
                updateLanguage: (language: string) => void,
                setCompanySettings: (settings: CompanySettingsModel) => void,
                skipSuccessToast: boolean = false
            ) => {
                const { settings, onboardingData } = get()
                if (!settings) return

                set({ isSubmittingOnboardingData: true })

                try {
                    const mergedSettings = { ...settings, ...partialOnboardingData } as CompanySettingsModel
                    await CompanySettingsApi.update(mergedSettings)

                    const updatedSettings = await CompanySettingsApi.get()

                    set({
                        settings: updatedSettings,
                        onboardingData: {
                            ...onboardingData!,
                            ...updatedSettings,
                        },
                    })

                    setCompanySettings({ ...updatedSettings })
                    updateLanguage(updatedSettings.defaultUiLanguage)

                    if (!skipSuccessToast) {
                        toastSuccess('Settings.Save.Message.successUpdate')
                    }
                } finally {
                    set({ isSubmittingOnboardingData: false })
                }
            },

            setPendingAutofill: (partial: Partial<OnboardingCompanyData> | null) => {
                const { onboardingData } = get()
                if (partial) {
                    set({
                        pendingAutofill: partial,
                        onboardingData: onboardingData ? { ...onboardingData, ...partial } : onboardingData,
                    })
                } else {
                    set({ pendingAutofill: null })
                }
            },

            reset: () => {
                set({ ...defaultState })
            },
        }),
        {
            name: 'onboarding-storage',
            // Only persist the showOnboardingOnStart flag, not the entire state
            partialize: state => ({
                showOnboardingOnStart: state.showOnboardingOnStart,
            }),
        }
    )
)
